import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import { Loader2, Calendar, Users, TrendingUp, Plus, Edit, Trash2, XCircle } from "lucide-react";

const COLUMNAS_META = {
  nuevo: { label: "Nuevo", color: "var(--brand-info)", bg: "var(--brand-info-soft)" },
  cotizado: { label: "Cotizado", color: "var(--brand-info)", bg: "var(--brand-info-soft)" },
  contratado: { label: "Contratado", color: "#8C6735", bg: "var(--brand-accent-soft)" },
  confirmado: { label: "Confirmado", color: "var(--brand-success)", bg: "var(--brand-success-soft)" },
  en_evento: { label: "En evento", color: "var(--brand-primary-dark)", bg: "var(--brand-primary-soft)" },
  finalizado: { label: "Finalizado", color: "var(--brand-text-muted)", bg: "var(--brand-surface-muted)" },
  cancelado: { label: "Cancelado", color: "#B4232C", bg: "var(--brand-danger-soft, #FDECEC)" },
};

const Pipeline = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/pipeline`); setData(r.data); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchData(); }, [fetchData]);

  const eliminar = async (ev, e) => {
    e.stopPropagation();
    if (!window.confirm(`¿Quitar "${ev.nombre || "este evento"}" del pipeline?\n\nNo se borra: queda accesible en Eventos. Sólo deja de aparecer aquí.`)) return;
    try {
      await axios.post(`${API}/pipeline/${ev.evento_id}/archivar`);
      toast.success("Quitado del pipeline (sigue en Eventos)");
      fetchData();
    } catch (err) {
      toast.error("No se pudo archivar: " + (err?.response?.data?.detail || err.message));
    }
  };

  // #275380 · Las tarjetas que son cotizaciones sin evento (origen "cotizacion") traen
  // evento_id = cotizacion_id; abrirlas como evento mostraba "Este evento ya no existe".
  // Se abren en la cotización, donde está el botón "Crear evento".
  const abrir = (ev) => {
    if (ev.origen === "cotizacion") navigate(`/panel/cotizar/${ev.cotizacion_id}`);
    else navigate(`/panel/eventos/${ev.evento_id}`);
  };
  const editar = (ev, e) => {
    e.stopPropagation();
    abrir(ev);
  };

  if (loading || !data) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin" style={{ color: "var(--brand-primary)" }} /></div>;

  const totalPipeline = Object.values(data.columnas).reduce((s, c) => s + c.total, 0);
  const totalConfirmado = data.columnas.confirmado.total + data.columnas.en_evento.total + data.columnas.finalizado.total;

  return (
    <div className="max-w-[1600px] mx-auto pb-12">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--brand-primary)" }}>Pipeline de ventas</p>
          <h1 className="font-display text-4xl md:text-5xl leading-none" style={{ color: "var(--brand-text)" }}>Cotizaciones</h1>
          <p className="text-sm mt-2" style={{ color: "var(--brand-text-muted)" }}>Visualiza el flujo de ventas: del lead nuevo al evento finalizado.</p>
        </div>
        <button onClick={() => navigate("/panel/cotizar")} className="eo-btn eo-btn-primary" data-testid="btn-nueva-cot">
          <Plus className="w-4 h-4" /> Nueva cotización
        </button>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Kpi label="Pipeline total" value={`$${totalPipeline.toLocaleString("es-MX")}`} icon={TrendingUp} accent="primary" />
        <Kpi label="Confirmado/Cobrado" value={`$${totalConfirmado.toLocaleString("es-MX")}`} icon={TrendingUp} accent="success" />
        <Kpi label="En negociación" value={data.columnas.cotizado.count + data.columnas.contratado.count} icon={Calendar} accent="warning" />
        <Kpi label="Total eventos activos" value={Object.values(data.columnas).reduce((s, c) => s + c.count, 0)} icon={Users} accent="info" />
      </div>

      {/* KANBAN */}
      <div className="grid grid-flow-col auto-cols-[minmax(260px,1fr)] gap-3 overflow-x-auto pb-4" data-testid="pipeline-kanban">
        {data.orden.map(k => {
          const col = data.columnas[k]; const meta = COLUMNAS_META[k];
          return (
            <div key={k} className="flex flex-col rounded-2xl" style={{ background: "var(--brand-surface-muted)", minHeight: 400 }} data-testid={`col-${k}`}>
              <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: `1px solid var(--brand-border)`, background: meta.bg, borderTopLeftRadius: 16, borderTopRightRadius: 16 }}>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wider" style={{ color: meta.color }}>{meta.label}</p>
                  <p className="text-[10px]" style={{ color: "var(--brand-text-muted)" }}>{col.count} {col.count === 1 ? "evento" : "eventos"} · ${col.total.toLocaleString("es-MX")}</p>
                </div>
              </div>
              <div className="flex-1 p-2 space-y-2 overflow-y-auto" style={{ maxHeight: "70vh" }}>
                {col.eventos.length === 0 ? (
                  <p className="text-xs text-center py-8" style={{ color: "var(--brand-text-muted)" }}>Sin eventos en esta fase</p>
                ) : col.eventos.map(ev => (
                  <div key={ev.evento_id} className="eo-card eo-card-hover p-3 cursor-pointer" onClick={() => abrir(ev)} data-testid={`evento-${ev.evento_id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate" style={{ color: "var(--brand-text)" }}>{ev.nombre || "Sin nombre"}</p>
                        {ev.cliente_nombre && <p className="text-xs truncate" style={{ color: "var(--brand-text-muted)" }}>{ev.cliente_nombre}</p>}
                      </div>
                      <div className="flex gap-0.5 shrink-0">
                        <button
                          onClick={(e) => editar(ev, e)}
                          className="p-1 rounded hover:bg-[var(--brand-surface-muted)]"
                          title="Editar"
                          data-testid={`btn-editar-${ev.evento_id}`}
                        >
                          <Edit className="w-3.5 h-3.5" style={{ color: "var(--brand-text-muted)" }} />
                        </button>
                        {ev.estatus !== "cancelado" && ev.estatus !== "cancelada" && (
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (!confirm(`¿Marcar "${ev.nombre}" como CANCELADO?`)) return;
                              try {
                                if (ev.origen === "cotizacion") await axios.put(`${API}/cotizaciones/${ev.cotizacion_id}`, { estatus: "cancelada" });
                                else await axios.put(`${API}/eventos/${ev.evento_id}`, { estatus: "cancelado" });
                                fetchData();
                              } catch { alert("Error al cancelar"); }
                            }}
                            className="p-1 rounded hover:bg-amber-50"
                            title="Marcar como cancelado"
                            data-testid={`btn-cancelar-${ev.evento_id}`}
                          >
                            <XCircle className="w-3.5 h-3.5 text-amber-600" />
                          </button>
                        )}
                        <button
                          onClick={(e) => eliminar(ev, e)}
                          className="p-1 rounded hover:bg-red-50"
                          title="Eliminar"
                          data-testid={`btn-eliminar-${ev.evento_id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5 text-red-500" />
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-2 text-[11px]" style={{ color: "var(--brand-text-muted)" }}>
                      <span>{ev.fecha || "—"}</span>
                      <span className="font-medium" style={{ color: "var(--brand-text)" }}>${(ev.total || 0).toLocaleString("es-MX")}</span>
                    </div>
                    {ev.saldo > 0 && (ev.estado_pago === "parcial" || ev.estado_pago === "sin_anticipo") && (
                      <div className="mt-2 pt-2 flex items-center justify-between text-[10px]" style={{ borderTop: "1px dashed var(--brand-border)" }}>
                        <span style={{ color: "var(--brand-warning)" }}>Saldo</span>
                        <span style={{ color: "var(--brand-warning)" }}>${(ev.saldo || 0).toLocaleString("es-MX")}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const Kpi = ({ label, value, icon: Icon, accent }) => {
  const map = {
    primary: { bg: "var(--brand-primary-soft)", color: "var(--brand-primary)" },
    success: { bg: "var(--brand-success-soft)", color: "var(--brand-success)" },
    warning: { bg: "var(--brand-warning-soft)", color: "#8C5E0B" },
    info: { bg: "var(--brand-info-soft)", color: "var(--brand-info)" },
  }[accent];
  return (
    <div className="eo-card p-4 flex items-center gap-3">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: map.bg }}>
        <Icon className="w-5 h-5" style={{ color: map.color }} />
      </div>
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--brand-text-muted)" }}>{label}</p>
        <p className="font-display text-xl truncate" style={{ color: "var(--brand-text)" }}>{value}</p>
      </div>
    </div>
  );
};

export default Pipeline;
